/**
 * DemandGenAdEditor Component
 * Edit Demand Gen ad text assets and images
 *
 * Supports three ad types:
 * - MULTI_ASSET: Headlines, descriptions, and multiple image formats
 * - VIDEO: Headlines, long headlines, descriptions, and YouTube videos
 * - CAROUSEL: Cards with headlines and images
 *
 * Pattern follows RDAEditor for consistency.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TrashIcon, CloseIcon } from '@/components/ui/Icons';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  getDemandGenAd,
  updateDemandGenAd,
  getLinkedImagesByEntity,
  type RDAImageAsset,
  type GetDemandGenAdResponse,
  type DemandGenAdType,
} from '@/services/api/assets';
import { debug, toast } from '@/utils';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import { pendingKey, type PendingTargeted } from '@/types/media';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { DEMAND_GEN_LIMITS } from '@/constants/googleAdsLimits';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';

interface DemandGenAdEditorProps {
  memberUuid: string;
  campaignId?: string;
  adGroupId: string;
  adId: string;
  customerId?: string;
  adGroupName?: string;
  initialData?: GetDemandGenAdResponse;
  onSaved: () => void;
  onCancel: () => void;
}

const HEADLINE_LIMIT = DEMAND_GEN_LIMITS.HEADLINE_CHARS;
const LONG_HEADLINE_LIMIT = DEMAND_GEN_LIMITS.LONG_HEADLINE_CHARS;
const DESCRIPTION_LIMIT = DEMAND_GEN_LIMITS.DESCRIPTION_CHARS;
const BUSINESS_NAME_LIMIT = DEMAND_GEN_LIMITS.BUSINESS_NAME_CHARS;
const MIN_HEADLINES = DEMAND_GEN_LIMITS.MIN_HEADLINES;
const MAX_HEADLINES = DEMAND_GEN_LIMITS.MAX_HEADLINES;
const MIN_DESCRIPTIONS = DEMAND_GEN_LIMITS.MIN_DESCRIPTIONS;
const MAX_DESCRIPTIONS = DEMAND_GEN_LIMITS.MAX_DESCRIPTIONS;
const MIN_LONG_HEADLINES = DEMAND_GEN_LIMITS.MIN_LONG_HEADLINES;
const MAX_LONG_HEADLINES = DEMAND_GEN_LIMITS.MAX_LONG_HEADLINES;

// Image picker modes
type ImagePickerMode = 'marketing' | 'square' | 'portrait' | 'logo';

// Helper component for image preview (reuses ImageThumbnailWithActions)
interface ImagePreviewProps {
  imageUrl?: string;
  assetId: string;
  aspectRatio: string;
  onRemove: () => void;
  label?: string;
}

function ImagePreview({ imageUrl, aspectRatio, onRemove, label }: ImagePreviewProps) {
  const getSize = () => {
    switch (aspectRatio) {
      case '1.91:1': return { width: 76, height: 40 };
      case '1:1': return { width: 64, height: 64 };
      case '4:5': return { width: 51, height: 64 };
      default: return { width: 64, height: 64 };
    }
  };

  const size = getSize();

  return (
    <ImageThumbnailWithActions
      imageUrl={imageUrl}
      alt={label || 'Image'}
      size={size}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image from the ad?"
    />
  );
}

// Pending-crop preview (CSS-cropped, no GCS write yet).
interface PendingPreviewProps {
  pending: PendingTargeted;
  aspectRatio: string;
  onRemove: () => void;
}

function PendingPreview({ pending, aspectRatio, onRemove }: PendingPreviewProps) {
  const getSize = () => {
    switch (aspectRatio) {
      case '1.91:1': return { width: 76, height: 40 };
      case '1:1': return { width: 64, height: 64 };
      case '4:5': return { width: 51, height: 64 };
      default: return { width: 64, height: 64 };
    }
  };
  return (
    <ImageThumbnailWithActions
      alt={pending.source.alt_text || pending.source.original_filename}
      size={getSize()}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image from the ad?"
    >
      <CroppedThumbnail source={pending.source} cropBox={pending.cropBox} />
    </ImageThumbnailWithActions>
  );
}

export function DemandGenAdEditor({
  memberUuid,
  campaignId,
  adGroupId,
  adId,
  customerId,
  adGroupName: initialAdGroupName,
  initialData,
  onSaved,
  onCancel,
}: DemandGenAdEditorProps) {
  const materializePending = useMaterializePendingTargeted();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ad type
  const [adType, setAdType] = useState<DemandGenAdType>('MULTI_ASSET');

  // Common fields
  const [adName, setAdName] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [callToAction, setCallToAction] = useState<string>('');
  const [finalUrl, setFinalUrl] = useState<string>('');
  const [adGroupName, setAdGroupName] = useState<string>(initialAdGroupName || '');

  // Multi-Asset and Video text
  const [headlines, setHeadlines] = useState<string[]>([]);
  const [descriptions, setDescriptions] = useState<string[]>([]);

  // Video-specific
  const [longHeadlines, setLongHeadlines] = useState<string[]>([]);
  const [videoIds, setVideoIds] = useState<string[]>([]);

  // Image data - original images from Google Ads
  const [marketingImages, setMarketingImages] = useState<RDAImageAsset[]>([]);
  const [squareMarketingImages, setSquareMarketingImages] = useState<RDAImageAsset[]>([]);
  const [portraitMarketingImages, setPortraitMarketingImages] = useState<RDAImageAsset[]>([]);
  const [logoImages, setLogoImages] = useState<RDAImageAsset[]>([]);

  // New images from Media Library
  const [newMarketingPending, setNewMarketingPending] = useState<PendingTargeted[]>([]);
  const [newSquarePending, setNewSquarePending] = useState<PendingTargeted[]>([]);
  const [newPortraitPending, setNewPortraitPending] = useState<PendingTargeted[]>([]);
  const [newLogoPending, setNewLogoPending] = useState<PendingTargeted[]>([]);

  // Carousel cards (for CAROUSEL type)
  const [carouselCards, setCarouselCards] = useState<Array<{
    headline: string;
    imageId: string;
    imageUrl?: string;
    finalUrl: string;
  }>>([]);

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState<ImagePickerMode>('marketing');

  // Load ad data
  useEffect(() => {
    async function loadAd() {
      setLoading(true);
      setError(null);

      try {
        // Use initialData if provided, otherwise fetch from API
        const adResponse = initialData?.success
          ? initialData
          : await getDemandGenAd({
              member_uuid: memberUuid,
              ad_group_id: adGroupId,
              ad_id: adId,
              customer_id: customerId,
            });

        // Fetch linked images for thumbnails
        const linkedImagesResponse = await getLinkedImagesByEntity({
          member_uuid: memberUuid,
          entity_type: 'ad',
          entity_id: adId,
          customer_id: customerId,
        });

        if (adResponse.success) {
          console.log('[DemandGenAdEditor] API Response:', {
            ad_name: adResponse.ad_name,
            business_name: adResponse.business_name,
            call_to_action: adResponse.call_to_action,
            ad_type: adResponse.ad_type,
            headlines: adResponse.headlines,
          });
          setAdType(adResponse.ad_type || 'MULTI_ASSET');
          setAdName(adResponse.ad_name || '');
          setBusinessName(adResponse.business_name || '');
          setCallToAction(adResponse.call_to_action || '');
          setFinalUrl(adResponse.final_urls?.[0] || '');
          setAdGroupName(adResponse.ad_group_name || initialAdGroupName || '');
          setHeadlines(adResponse.headlines || []);
          setDescriptions(adResponse.descriptions || []);
          setLongHeadlines(adResponse.long_headlines || []);
          setVideoIds(adResponse.video_ids || []);

          // Build thumbnail lookup
          const thumbnailMap = new Map<string, string>();
          if (linkedImagesResponse.success && linkedImagesResponse.images) {
            for (const img of linkedImagesResponse.images) {
              if (img.google_ads_asset_id && img.thumbnail_url) {
                thumbnailMap.set(img.google_ads_asset_id, img.thumbnail_url);
              }
            }
          }

          const mergeUrls = (assets: RDAImageAsset[]): RDAImageAsset[] => {
            return assets.map((asset) => ({
              ...asset,
              url: thumbnailMap.get(asset.asset_id) || asset.url,
            }));
          };

          setMarketingImages(mergeUrls(adResponse.marketing_images || []));
          setSquareMarketingImages(mergeUrls(adResponse.square_marketing_images || []));
          setPortraitMarketingImages(mergeUrls(adResponse.portrait_marketing_images || []));
          setLogoImages(mergeUrls(adResponse.logo_images || []));

          // Handle carousel cards
          if (adResponse.carousel_cards) {
            setCarouselCards(adResponse.carousel_cards.map(card => ({
              headline: card.headline,
              imageId: card.asset_id || card.image_id || '',  // Use asset_id (Google Ads) or image_id (Media Library)
              finalUrl: card.final_url || '',
            })));
          }

          debug.log('Loaded Demand Gen ad data', adResponse);
        } else {
          setError(adResponse.error || 'Failed to load ad');
        }
      } catch (err) {
        console.error('Error loading Demand Gen ad:', err);
        setError('Failed to load ad data');
      } finally {
        setLoading(false);
      }
    }

    loadAd();
  }, [memberUuid, adGroupId, adId, customerId, initialData, initialAdGroupName]);

  // Text field handlers (similar to RDAEditor)
  const handleHeadlineChange = (index: number, text: string) => {
    if (text.length > HEADLINE_LIMIT) return;
    const updated = [...headlines];
    updated[index] = text;
    setHeadlines(updated);
  };

  const handleAddHeadline = () => {
    if (headlines.length >= MAX_HEADLINES) {
      toast.warning(`Maximum ${MAX_HEADLINES} headlines allowed`);
      return;
    }
    setHeadlines([...headlines, '']);
  };

  const handleRemoveHeadline = (index: number) => {
    if (headlines.length <= MIN_HEADLINES) {
      toast.warning(`Minimum ${MIN_HEADLINES} headline required`);
      return;
    }
    setHeadlines(headlines.filter((_, i) => i !== index));
  };

  const handleDescriptionChange = (index: number, text: string) => {
    if (text.length > DESCRIPTION_LIMIT) return;
    const updated = [...descriptions];
    updated[index] = text;
    setDescriptions(updated);
  };

  const handleAddDescription = () => {
    if (descriptions.length >= MAX_DESCRIPTIONS) {
      toast.warning(`Maximum ${MAX_DESCRIPTIONS} descriptions allowed`);
      return;
    }
    setDescriptions([...descriptions, '']);
  };

  const handleRemoveDescription = (index: number) => {
    if (descriptions.length <= MIN_DESCRIPTIONS) {
      toast.warning(`Minimum ${MIN_DESCRIPTIONS} description required`);
      return;
    }
    setDescriptions(descriptions.filter((_, i) => i !== index));
  };

  // Long headline handlers (for Video ads)
  const handleLongHeadlineChange = (index: number, text: string) => {
    if (text.length > LONG_HEADLINE_LIMIT) return;
    const updated = [...longHeadlines];
    updated[index] = text;
    setLongHeadlines(updated);
  };

  const handleAddLongHeadline = () => {
    if (longHeadlines.length >= MAX_LONG_HEADLINES) {
      toast.warning(`Maximum ${MAX_LONG_HEADLINES} long headlines allowed`);
      return;
    }
    setLongHeadlines([...longHeadlines, '']);
  };

  const handleRemoveLongHeadline = (index: number) => {
    if (longHeadlines.length <= MIN_LONG_HEADLINES) {
      toast.warning(`Minimum ${MIN_LONG_HEADLINES} long headline required`);
      return;
    }
    setLongHeadlines(longHeadlines.filter((_, i) => i !== index));
  };

  // Video ID handlers
  const handleVideoIdChange = (index: number, url: string) => {
    const updated = [...videoIds];
    updated[index] = url;
    setVideoIds(updated);
  };

  const handleAddVideoId = () => {
    if (videoIds.length >= 5) {
      toast.warning('Maximum 5 videos allowed');
      return;
    }
    setVideoIds([...videoIds, '']);
  };

  const handleRemoveVideoId = (index: number) => {
    if (videoIds.length <= 1) {
      toast.warning('At least 1 video is required');
      return;
    }
    setVideoIds(videoIds.filter((_, i) => i !== index));
  };

  // Image handlers (similar to RDAEditor)
  const openImagePicker = (mode: ImagePickerMode) => {
    setImagePickerMode(mode);
    setShowImagePicker(true);
  };

  // Picker confirmation — store pending crops in memory only; no GCS write.
  // Materialization happens at Save Changes (handleSave).
  const handleImageConfirm = (pending: PendingTargeted[]) => {
    switch (imagePickerMode) {
      case 'marketing':
        setNewMarketingPending(pending);
        break;
      case 'square':
        setNewSquarePending(pending);
        break;
      case 'portrait':
        setNewPortraitPending(pending);
        break;
      case 'logo':
        setNewLogoPending(pending);
        break;
    }
    setShowImagePicker(false);
  };

  const handleRemoveOriginalImage = (type: ImagePickerMode, assetId: string) => {
    switch (type) {
      case 'marketing':
        setMarketingImages(marketingImages.filter(img => img.asset_id !== assetId));
        break;
      case 'square':
        setSquareMarketingImages(squareMarketingImages.filter(img => img.asset_id !== assetId));
        break;
      case 'portrait':
        setPortraitMarketingImages(portraitMarketingImages.filter(img => img.asset_id !== assetId));
        break;
      case 'logo':
        setLogoImages(logoImages.filter(img => img.asset_id !== assetId));
        break;
    }
  };

  const handleRemoveNewImage = (type: ImagePickerMode, key: string) => {
    const filter = (prev: PendingTargeted[]) => prev.filter((p) => pendingKey(p) !== key);
    switch (type) {
      case 'marketing':
        setNewMarketingPending(filter);
        break;
      case 'square':
        setNewSquarePending(filter);
        break;
      case 'portrait':
        setNewPortraitPending(filter);
        break;
      case 'logo':
        setNewLogoPending(filter);
        break;
    }
  };

  // Map UI mode → spec placement_id. The TargetedImagePicker resolves the
  // aspect ratio from the placement key, so we don't track it here anymore.
  const placementForMode: Record<ImagePickerMode, string> = {
    marketing: 'marketing_image',
    square: 'square_marketing_image',
    portrait: 'portrait_marketing_image',
    logo: 'logo',
  };
  const MAX_SELECTION_FOR_MODE: Record<ImagePickerMode, number> = {
    marketing: 15,
    square: 15,
    portrait: 5,
    logo: 5,
  };
  const pickerConfig = { maxSelection: MAX_SELECTION_FOR_MODE[imagePickerMode] };

  // Validate and save
  const handleSave = async () => {
    // Validate based on ad type
    if (adType === 'MULTI_ASSET' || adType === 'VIDEO') {
      const validHeadlines = headlines.filter(h => h.trim());
      if (validHeadlines.length < MIN_HEADLINES) {
        toast.error(`At least ${MIN_HEADLINES} headline is required`);
        return;
      }
      if (headlines.some(h => !h.trim())) {
        toast.error('Please fill in all headline fields or remove empty ones');
        return;
      }

      const validDescriptions = descriptions.filter(d => d.trim());
      if (validDescriptions.length < MIN_DESCRIPTIONS) {
        toast.error(`At least ${MIN_DESCRIPTIONS} description is required`);
        return;
      }
      if (descriptions.some(d => !d.trim())) {
        toast.error('Please fill in all description fields or remove empty ones');
        return;
      }
    }

    if (adType === 'MULTI_ASSET') {
      const totalMarketing = marketingImages.length + newMarketingPending.length;
      const totalSquare = squareMarketingImages.length + newSquarePending.length;
      const totalLogo = logoImages.length + newLogoPending.length;
      if (totalMarketing < 1) {
        toast.error('At least 1 landscape image (1.91:1) is required');
        return;
      }
      if (totalSquare < 1) {
        toast.error('At least 1 square image (1:1) is required');
        return;
      }
      if (totalLogo < 1) {
        toast.error('At least 1 logo is required');
        return;
      }
    }

    if (adType === 'VIDEO') {
      const validLongHeadlines = longHeadlines.filter(h => h.trim());
      if (validLongHeadlines.length < MIN_LONG_HEADLINES) {
        toast.error(`At least ${MIN_LONG_HEADLINES} long headline is required`);
        return;
      }
      const validVideoIds = videoIds.filter(v => v.trim());
      if (validVideoIds.length < 1) {
        toast.error('At least 1 YouTube video is required');
        return;
      }
    }

    if (!businessName.trim()) {
      toast.error('Business name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Materialize pending crops just before submit. Each call writes one
      // Targeted MediaImage to GCS+DB; parallelized across buckets.
      const [marketingMaterialized, squareMaterialized, portraitMaterialized, logoMaterialized] =
        await Promise.all([
          materializePending(newMarketingPending),
          materializePending(newSquarePending),
          materializePending(newPortraitPending),
          materializePending(newLogoPending),
        ]);

      // Note: ad_name is NOT included - it's immutable in Google Ads API
      const response = await updateDemandGenAd({
        member_uuid: memberUuid,
        campaign_id: campaignId,
        ad_group_id: adGroupId,
        ad_id: adId,
        customer_id: customerId,
        ad_type: adType,
        business_name: businessName,
        call_to_action: callToAction || undefined,
        final_urls: finalUrl ? [finalUrl] : undefined,
        headlines: headlines.filter(h => h.trim()),
        descriptions: descriptions.filter(d => d.trim()),
        long_headlines: adType === 'VIDEO' ? longHeadlines.filter(h => h.trim()) : undefined,
        video_ids: adType === 'VIDEO' ? videoIds.filter(v => v.trim()) : undefined,
        marketing_images: marketingImages.map(img => img.asset_id),
        square_marketing_images: squareMarketingImages.map(img => img.asset_id),
        portrait_marketing_images: portraitMarketingImages.map(img => img.asset_id),
        logo_images: logoImages.map(img => img.asset_id),
        new_marketing_image_ids: marketingMaterialized.length > 0 ? marketingMaterialized.map((t) => t.id) : undefined,
        new_square_image_ids: squareMaterialized.length > 0 ? squareMaterialized.map((t) => t.id) : undefined,
        new_portrait_image_ids: portraitMaterialized.length > 0 ? portraitMaterialized.map((t) => t.id) : undefined,
        new_logo_image_ids: logoMaterialized.length > 0 ? logoMaterialized.map((t) => t.id) : undefined,
        trigger_data_sync: true,
      });

      if (response.success) {
        toast.success(response.message);
        onSaved();
      } else {
        setError(response.error || 'Failed to update ad');
        toast.error(response.error || 'Failed to update ad');
      }
    } catch (err) {
      console.error('Error updating Demand Gen ad:', err);
      setError('Failed to update ad');
      toast.error('Failed to update ad');
    } finally {
      setSaving(false);
    }
  };

  const getAdTypeLabel = () => {
    switch (adType) {
      case 'MULTI_ASSET': return 'Multi-Asset Ad';
      case 'VIDEO': return 'Video Ad';
      case 'CAROUSEL': return 'Carousel Ad';
      default: return 'Demand Gen Ad';
    }
  };

  // Error state with no data
  if (error && headlines.length === 0 && !loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-gray-900">Edit Demand Gen Ad</h2>
            <p className="text-sm text-gray-500">
              {getAdTypeLabel()} in <span className="font-semibold text-gray-700">{adGroupName || 'Ad Group'}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            aria-label="Close editor"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="text-red-600 mb-4">{error}</div>
        <Button onClick={onCancel} variant="secondary">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-900">Edit Demand Gen Ad</h2>
          <p className="text-sm text-gray-500">
            {getAdTypeLabel()} in <span className="font-semibold text-gray-700">{adGroupName || 'Ad Group'}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          aria-label="Close editor"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error message */}
      {!loading && error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Content */}
      {!loading && (
        <>
          {/* Ad Name - Read-only for existing ads (immutable in Google Ads API) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ad Name
            </label>
            <Input
              value={adName}
              disabled={true}
              className="bg-gray-50 text-gray-600"
              placeholder="(Not set)"
            />
            <p className="mt-1 text-xs text-gray-500">
              Ad names cannot be changed after ad creation.
            </p>
          </div>

          {/* Final URL */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Final URL
            </label>
            <Input
              value={finalUrl}
              onChange={(e) => setFinalUrl(e.target.value)}
              placeholder="https://example.com/landing-page"
            />
          </div>

          {/* Business Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={businessName}
                onChange={(e) => {
                  if (e.target.value.length <= BUSINESS_NAME_LIMIT) {
                    setBusinessName(e.target.value);
                  }
                }}
                placeholder="Your business name"
                maxLength={BUSINESS_NAME_LIMIT}
                className="flex-1"
              />
              <span className="text-xs text-gray-400 w-12">
                {businessName.length}/{BUSINESS_NAME_LIMIT}
              </span>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Call to Action
            </label>
            <select
              value={callToAction}
              onChange={(e) => setCallToAction(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Default</option>
              <option value="LEARN_MORE">Learn More</option>
              <option value="SIGN_UP">Sign Up</option>
              <option value="SHOP_NOW">Shop Now</option>
              <option value="GET_QUOTE">Get Quote</option>
              <option value="APPLY_NOW">Apply Now</option>
              <option value="CONTACT_US">Contact Us</option>
              <option value="DOWNLOAD">Download</option>
              <option value="BOOK_NOW">Book Now</option>
            </select>
          </div>

          {/* Headlines Section (Multi-Asset and Video) */}
          {(adType === 'MULTI_ASSET' || adType === 'VIDEO') && (
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Headlines ({headlines.length}/{MAX_HEADLINES})
                </label>
                <Button
                  onClick={handleAddHeadline}
                  variant="ghost"
                  size="sm"
                  disabled={headlines.length >= MAX_HEADLINES}
                  className="self-start sm:self-auto mt-1 sm:mt-0"
                >
                  + Add Headline
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {MIN_HEADLINES}-{MAX_HEADLINES} headlines, max {HEADLINE_LIMIT} characters each
              </p>
              <div className="space-y-3">
                {headlines.map((headline, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-6 flex-shrink-0">{index + 1}.</span>
                    <Input
                      value={headline}
                      onChange={(e) => handleHeadlineChange(index, e.target.value)}
                      placeholder={`Headline ${index + 1}`}
                      maxLength={HEADLINE_LIMIT}
                      className="flex-1"
                    />
                    <span className="text-xs text-gray-400 w-12 flex-shrink-0 hidden sm:block">
                      {headline.length}/{HEADLINE_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveHeadline(index)}
                      variant="ghost"
                      size="sm"
                      disabled={headlines.length <= MIN_HEADLINES}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Long Headlines (Video only) */}
          {adType === 'VIDEO' && (
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Long Headlines ({longHeadlines.length}/{MAX_LONG_HEADLINES})
                </label>
                <Button
                  onClick={handleAddLongHeadline}
                  variant="ghost"
                  size="sm"
                  disabled={longHeadlines.length >= MAX_LONG_HEADLINES}
                  className="self-start sm:self-auto mt-1 sm:mt-0"
                >
                  + Add Long Headline
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {MIN_LONG_HEADLINES}-{MAX_LONG_HEADLINES} long headlines, max {LONG_HEADLINE_LIMIT} characters each
              </p>
              <div className="space-y-3">
                {longHeadlines.map((headline, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 w-6 pt-2 flex-shrink-0">{index + 1}.</span>
                    <textarea
                      value={headline}
                      onChange={(e) => handleLongHeadlineChange(index, e.target.value)}
                      placeholder={`Long headline ${index + 1}`}
                      maxLength={LONG_HEADLINE_LIMIT}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                    />
                    <span className="text-xs text-gray-400 w-12 pt-2 flex-shrink-0">
                      {headline.length}/{LONG_HEADLINE_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveLongHeadline(index)}
                      variant="ghost"
                      size="sm"
                      disabled={longHeadlines.length <= MIN_LONG_HEADLINES}
                      className="text-red-500 hover:text-red-700 mt-1"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Descriptions (Multi-Asset and Video) */}
          {(adType === 'MULTI_ASSET' || adType === 'VIDEO') && (
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Descriptions ({descriptions.length}/{MAX_DESCRIPTIONS})
                </label>
                <Button
                  onClick={handleAddDescription}
                  variant="ghost"
                  size="sm"
                  disabled={descriptions.length >= MAX_DESCRIPTIONS}
                  className="self-start sm:self-auto mt-1 sm:mt-0"
                >
                  + Add Description
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {MIN_DESCRIPTIONS}-{MAX_DESCRIPTIONS} descriptions, max {DESCRIPTION_LIMIT} characters each
              </p>
              <div className="space-y-3">
                {descriptions.map((description, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 w-6 pt-2 flex-shrink-0">{index + 1}.</span>
                    <textarea
                      value={description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder={`Description ${index + 1}`}
                      maxLength={DESCRIPTION_LIMIT}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                    />
                    <span className="text-xs text-gray-400 w-12 pt-2 flex-shrink-0 hidden sm:block">
                      {description.length}/{DESCRIPTION_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveDescription(index)}
                      variant="ghost"
                      size="sm"
                      disabled={descriptions.length <= MIN_DESCRIPTIONS}
                      className="text-red-500 hover:text-red-700 mt-1"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* YouTube Videos (Video only) */}
          {adType === 'VIDEO' && (
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <VideoCameraIcon className="w-4 h-4 inline mr-1" />
                  YouTube Videos ({videoIds.length}/5)
                </label>
                <Button
                  onClick={handleAddVideoId}
                  variant="ghost"
                  size="sm"
                  disabled={videoIds.length >= 5}
                  className="self-start sm:self-auto mt-1 sm:mt-0"
                >
                  + Add Video
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Enter YouTube video URLs or IDs
              </p>
              <div className="space-y-3">
                {videoIds.map((videoUrl, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-6 flex-shrink-0">{index + 1}.</span>
                    <Input
                      value={videoUrl}
                      onChange={(e) => handleVideoIdChange(index, e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                      className="flex-1"
                    />
                    <Button
                      onClick={() => handleRemoveVideoId(index)}
                      variant="ghost"
                      size="sm"
                      disabled={videoIds.length <= 1}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images Section (Multi-Asset only) */}
          {adType === 'MULTI_ASSET' && (
            <div className="mb-6 space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Images
              </label>
              <p className="text-xs text-gray-500 -mt-2">
                Click on images to remove. Use "Add" buttons to select new images.
              </p>

              {/* Landscape Images (1.91:1) */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Landscape (1.91:1) <span className="text-red-500">*</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {marketingImages.length + newMarketingPending.length}/15 (min 1)
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {marketingImages.map((img) => (
                    <ImagePreview
                      key={`orig-marketing-${img.asset_id}`}
                      imageUrl={img.url}
                      assetId={img.asset_id}
                      aspectRatio="1.91:1"
                      onRemove={() => handleRemoveOriginalImage('marketing', img.asset_id)}
                    />
                  ))}
                  {newMarketingPending.map((p) => {
                    const k = pendingKey(p);
                    return (
                      <PendingPreview
                        key={`new-marketing-${k}`}
                        pending={p}
                        aspectRatio="1.91:1"
                        onRemove={() => handleRemoveNewImage('marketing', k)}
                      />
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => openImagePicker('marketing')}
                    className="w-20 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <PhotoIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Square Images (1:1) */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Square (1:1) <span className="text-red-500">*</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {squareMarketingImages.length + newSquarePending.length}/15 (min 1)
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {squareMarketingImages.map((img) => (
                    <ImagePreview
                      key={`orig-square-${img.asset_id}`}
                      imageUrl={img.url}
                      assetId={img.asset_id}
                      aspectRatio="1:1"
                      onRemove={() => handleRemoveOriginalImage('square', img.asset_id)}
                    />
                  ))}
                  {newSquarePending.map((p) => {
                    const k = pendingKey(p);
                    return (
                      <PendingPreview
                        key={`new-square-${k}`}
                        pending={p}
                        aspectRatio="1:1"
                        onRemove={() => handleRemoveNewImage('square', k)}
                      />
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => openImagePicker('square')}
                    className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <PhotoIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Portrait Images (4:5) - Optional */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Portrait (4:5)
                  </label>
                  <span className="text-xs text-gray-500">
                    {portraitMarketingImages.length + newPortraitPending.length}/5 (optional)
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {portraitMarketingImages.map((img) => (
                    <ImagePreview
                      key={`orig-portrait-${img.asset_id}`}
                      imageUrl={img.url}
                      assetId={img.asset_id}
                      aspectRatio="4:5"
                      onRemove={() => handleRemoveOriginalImage('portrait', img.asset_id)}
                    />
                  ))}
                  {newPortraitPending.map((p) => {
                    const k = pendingKey(p);
                    return (
                      <PendingPreview
                        key={`new-portrait-${k}`}
                        pending={p}
                        aspectRatio="4:5"
                        onRemove={() => handleRemoveNewImage('portrait', k)}
                      />
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => openImagePicker('portrait')}
                    className="w-12 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <PhotoIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Logo (1:1) */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Logo (1:1) <span className="text-red-500">*</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {logoImages.length + newLogoPending.length}/5 (min 1)
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {logoImages.map((img) => (
                    <ImagePreview
                      key={`orig-logo-${img.asset_id}`}
                      imageUrl={img.url}
                      assetId={img.asset_id}
                      aspectRatio="1:1"
                      onRemove={() => handleRemoveOriginalImage('logo', img.asset_id)}
                    />
                  ))}
                  {newLogoPending.map((p) => {
                    const k = pendingKey(p);
                    return (
                      <PendingPreview
                        key={`new-logo-${k}`}
                        pending={p}
                        aspectRatio="1:1"
                        onRemove={() => handleRemoveNewImage('logo', k)}
                      />
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => openImagePicker('logo')}
                    className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <PhotoIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Logo for Video ads */}
          {adType === 'VIDEO' && (
            <div className="mb-6">
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Logo (1:1) <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {logoImages.length + newLogoPending.length}/5 (min 1)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {logoImages.map((img) => (
                  <ImagePreview
                    key={`orig-logo-${img.asset_id}`}
                    imageUrl={img.url}
                    assetId={img.asset_id}
                    aspectRatio="1:1"
                    onRemove={() => handleRemoveOriginalImage('logo', img.asset_id)}
                  />
                ))}
                {newLogoPending.map((p) => {
                  const k = pendingKey(p);
                  return (
                    <PendingPreview
                      key={`new-logo-${k}`}
                      pending={p}
                      aspectRatio="1:1"
                      onRemove={() => handleRemoveNewImage('logo', k)}
                    />
                  );
                })}
                <button
                  type="button"
                  onClick={() => openImagePicker('logo')}
                  className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Carousel Cards (Carousel only) - Read-only for now */}
          {adType === 'CAROUSEL' && carouselCards.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Carousel Cards ({carouselCards.length})
              </label>
              <div className="space-y-2">
                {carouselCards.map((card, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="text-sm font-medium">{card.headline}</p>
                    <p className="text-xs text-gray-500 truncate">{card.finalUrl}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Carousel card editing coming soon. Cards can be managed when creating a new ad.
              </p>
            </div>
          )}

          {/* Image Picker Modal — TargetedImagePicker shows the crop modal
              after pick and returns `PendingTargeted[]` (in-memory source +
              placementKey + cropBox). The editor materializes per-placement
              Targeted derivatives only at Save, so abandoned crops never
              reach GCS. */}
          <TargetedImagePicker
            isOpen={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            usageContext={{
              platform: 'google_ads',
              placementKey: `google_ads.${placementForMode[imagePickerMode]}`,
            }}
            onConfirm={handleImageConfirm}
            maxSelection={pickerConfig.maxSelection}
          />

          {/* Save/Cancel Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={onCancel} variant="secondary" disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default DemandGenAdEditor;
